import {
    ContactLinksFetchStartAction,
    EventLogByChannelNameStartAction,
    EventLogParams,
    EventLogParamsByChannelName,
    EventLogStartAction,
    EventLogTotalEventCounts,
    EventLogTotalResultCounts,
    ExportLinkContactsAction,
    ExportLinksAction,
    GetReportsLocationStatsByNameStartAction,
    LinkContactsAllStartAction,
    LinkContactsStartAction,
    ReportsFetchStartAction,
    ReportsState,
    ResendStartAction,
    UrlSearchEventsActiveParams,
    UrlSearchInProgressActiveParams,
    UrlSearchParams,
} from '../store/reports/types';
import {
    ContactLinksLoadError,
    ContactLinksLoadSuccess,
    EventLogByChannelNameError,
    EventLogByChannelNameSuccess,
    EventLogError,
    EventLogSuccess,
    GetReportsLocationStatsByNameSuccess,
    LoadLinkContactsAllError,
    LoadLinkContactsAllSuccess,
    LoadLinkContactsError,
    LoadLinkContactsSuccess,
    ReportsLoadError,
    ReportsLoadSuccess,
    ResendError,
    ResendSuccess,
} from '../store/reports/actions';
import { DEFAULT_PER_PAGE_COUNT, SHORT_DATE_FORMAT, TotalEventHeaders } from '../services/constants';
import { HideToast, ShowToast } from '../store/toast/actions';
import { call, delay, fork, put, select, takeLatest } from 'redux-saga/effects';
import {
    checkTaskStatus,
    exportLinkContacts,
    exportLinks,
    getLinkContacts,
    getReportsLocationStatsByName,
    loadContactLinks,
    loadEventLogByChannelName,
    loadEventsGET,
    resendByMessageid,
} from '../services/ElasticApi';

import { ApplicationState } from '@api/store';
import { ExportStatus } from '@dev/ee-api/ee-api-models';
import { ExportsPaths } from '@api/services/dictionaries/paths';
import { push as Push } from 'redux-first-history';
import { calculateOffsetAndLimit } from '@api/services/tools/handleUrlParams';
import dayjs from '../services/tools/dayjs';
import i18n from '@api/i18n';
import { isEmpty } from 'lodash-es';
import { isNil } from 'lodash';
import { stringify } from 'query-string';

function* loadReports(action: ReportsFetchStartAction): any {
    const { log }: ReportsState = yield select((store: ApplicationState) => store?.reports);
    const currentEventLog = yield select((store: ApplicationState) => store?.reports?.eventLog);

    const apiParams = action.payload.params;

    if (action.payload.params.offset > 0) {
        apiParams.to = dayjs.utc(currentEventLog?.Recipients?.at?.(-1)?.EventDate).toJSON();
    }
    try {
        const resp = yield call(loadEventsGET, apiParams as any);
        const counts = Number(resp.headers[TotalEventHeaders.TotalCount]) || 0;

        let updatedLogs = resp.data;
        const hasMoreLogs = isEmpty(resp?.data?.Recipients) ? false : resp?.data?.Recipients?.length % DEFAULT_PER_PAGE_COUNT === 0;

        if (action.payload.params.offset > 0 && action.payload?.params?.infinityScroll) {
            updatedLogs = { ...resp.data, Recipients: [...log.Recipients, ...resp?.data?.Recipients] };
        }
        yield put(ReportsLoadSuccess(updatedLogs, counts, action.payload.params.statuses, hasMoreLogs));
    } catch (e) {
        console.log(e);
        yield put(ReportsLoadError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchLoadReports(): any {
    yield takeLatest('@@reports/FETCH_START', loadReports);
}

const extractTotalEventCounts = (headers: any): EventLogTotalEventCounts => {
    return {
        sending: Number(headers[TotalEventHeaders.SendingCount]) || 0,
        readytosend: Number(headers[TotalEventHeaders.ReadytosendCount]) || 0,
        waitingtoretry: Number(headers[TotalEventHeaders.WaitingtoretryCount]) || 0,
        all: Number(headers[TotalEventHeaders.AllCount]) || 0,
        error: Number(headers[TotalEventHeaders.ErrorCount]) || 0,
        sent: Number(headers[TotalEventHeaders.SentCount]) || 0,
        opened: Number(headers[TotalEventHeaders.OpenedCount]) || 0,
        clicked: Number(headers[TotalEventHeaders.ClickedCount]) || 0,
        unsubscribed: Number(headers[TotalEventHeaders.UnsubscribedCount]) || 0,
        abuseReport: Number(headers[TotalEventHeaders.AbuseReportCount]) || 0,
    };
};

const extractTotalResultCounts = (headers: any): EventLogTotalResultCounts => {
    return {
        total: Number(headers[TotalEventHeaders.TotalCount]),
        allResultCount: Number(headers[TotalEventHeaders.AllCount]),
        accountproblem: Number(headers[TotalEventHeaders.AccountproblemCount]),
        blacklisted: Number(headers[TotalEventHeaders.BlacklistedCount]),
        codeerror: Number(headers[TotalEventHeaders.CodeerrorCount]),
        connectionproblem: Number(headers[TotalEventHeaders.ConnectionproblemCount]),
        connectionterminated: Number(headers[TotalEventHeaders.ConnectionterminatedCount]),
        dnsproblem: Number(headers[TotalEventHeaders.DnsproblemCount]),
        error: Number(headers[TotalEventHeaders.ErrorCount]),
        greylisted: Number(headers[TotalEventHeaders.GreylistedCount]),
        ignore: Number(headers[TotalEventHeaders.IgnoreCount]),
        manualcancel: Number(headers[TotalEventHeaders.ManualcancelCount]),
        nomailbox: Number(headers[TotalEventHeaders.NomailboxCount]),
        notdelivered: Number(headers[TotalEventHeaders.NotdeliveredCount]),
        notdeliveredcancelled: Number(headers[TotalEventHeaders.NotdeliveredcancelledCount]),
        spam: Number(headers[TotalEventHeaders.SpamCount]),
        spfproblem: Number(headers[TotalEventHeaders.SpfproblemCount]),
        throttled: Number(headers[TotalEventHeaders.ThrottledCount]),
        timeout: Number(headers[TotalEventHeaders.TimeoutCount]),
        unknown: Number(headers[TotalEventHeaders.UnknownCount]),
    };
};

function* loadEventsLog(action: EventLogStartAction): any {
    try {
        const { payload } = action;
        const currentEventLog = yield select((store: ApplicationState) => store?.reports?.eventLog);
        const { offset, limit, statuses, messageCategory, email, channelName } = payload.params;
        const activePage = (offset + limit) / limit;

        let to = null;
        if (offset > 0) {
            to = dayjs.utc(currentEventLog?.Recipients?.at?.(-1)?.EventDate).toJSON();
        }
        const eventLogData = yield call(loadEventsGET, {
            limit,
            to,
            // offset,
            channelName,
            statuses,
            messageCategory,
            email,
        });

        const eventsCount = extractTotalEventCounts(eventLogData.headers);
        const resultsCount = extractTotalResultCounts(eventLogData.headers);

        const urlQueryParams: UrlSearchInProgressActiveParams = {
            page: activePage,
            perPage: limit,
            statuses,
        };

        if (messageCategory) {
            urlQueryParams.result = messageCategory;
        }

        if (email) {
            urlQueryParams.search = email;
        }

        yield put(Push({ search: stringify(urlQueryParams as any) }));

        const eventLogParams: EventLogParams = {
            offset,
            limit,
            statuses,
            messageCategory,
            email,
        };

        yield put(EventLogSuccess(eventLogData.data, eventsCount, resultsCount, eventLogParams));
    } catch (e) {
        console.log(e);
        yield put(EventLogError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchLoadEventsLog(): any {
    yield takeLatest('@@reports/EVENT_LOG_START', loadEventsLog);
}

function* loadEventsLogByChannelName(action: EventLogByChannelNameStartAction): any {
    try {
        const { payload } = action;
        const { offset, limit, statuses, messageCategory, email, channelName, lastEvent, from, to } = payload.params;
        const activePage = (offset + limit) / limit;

        const eventLogData = yield call(loadEventLogByChannelName, {
            limit,
            offset,
            channelName,
            statuses,
            messageCategory,
            lastEvent,
            email,
            from,
            to,
        });

        const eventsCount = extractTotalEventCounts(eventLogData.headers);
        const resultsCount = extractTotalResultCounts(eventLogData.headers);

        const urlQueryParams: UrlSearchEventsActiveParams = {
            page: activePage,
            perPage: limit,
        };

        if (statuses) {
            urlQueryParams.events = statuses;
        }

        if (messageCategory) {
            urlQueryParams.result = messageCategory;
        }

        if (!isNil(email)) {
            urlQueryParams.search = email;
        }

        if (!isNil(lastEvent)) {
            urlQueryParams.lastEvent = lastEvent;
        }

        if (from) {
            urlQueryParams.from = from;
        }

        if (to) {
            urlQueryParams.to = to;
        }

        yield put(Push({ search: stringify(urlQueryParams as any) }));

        const eventLogParams: EventLogParamsByChannelName = {
            offset,
            limit,
            statuses,
            messageCategory,
            email,
            lastEvent,
            channelName,
            from,
            to,
        };

        yield put(EventLogByChannelNameSuccess(eventLogData.data, eventsCount, resultsCount, eventLogParams));
    } catch (e) {
        console.log(e);
        yield put(EventLogByChannelNameError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchLoadEventsLogByChannelName(): any {
    yield takeLatest('@@reports/EVENT_LOG_BY_CHANNELNAME_START', loadEventsLogByChannelName);
}

function* loadContactLinkClicks(action: ContactLinksFetchStartAction): any {
    try {
        const links = yield call(loadContactLinks, action.payload.params);
        const counts = Number(links.headers[TotalEventHeaders.TotalCount]) || 0;

        yield put(ContactLinksLoadSuccess(links.data, counts));
    } catch (e) {
        console.log(e);
        yield put(ContactLinksLoadError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchLoadLinkClicks(): any {
    yield takeLatest('@@reports/FETCH_CONTACT_LINKS_START', loadContactLinkClicks);
}

function* exportLink(action: ExportLinksAction): any {
    const { payload } = action;
    let checkExportStatus = true;

    try {
        const resp = yield call(exportLinks, payload);

        while (checkExportStatus) {
            yield delay(5000);
            const statusResp = yield call(checkTaskStatus, resp.data.PublicExportID);
            const status: ExportStatus = statusResp.data;

            if (status === ExportStatus.Ready) {
                checkExportStatus = false;
            }

            if (status === ExportStatus.Error || status === ExportStatus.Expired) {
                throw new Error(statusResp);
            }
        }

        yield put(Push(ExportsPaths.Exports));

        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('linksexported'),
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}
function* watchExportLinks(): any {
    yield takeLatest('@@reports/EXPORT_LINKS', exportLink);
}

function* fetchLinkContactsList(action: LinkContactsStartAction): any {
    try {
        const { payload } = action;
        const { from, to, activePage, perPage } = payload;

        const { offset, limit } = calculateOffsetAndLimit(activePage, perPage);
        const linkContactsList = yield call(getLinkContacts, {
            limit,
            offset,
            channelName: payload.channelName,
            link: payload.link,
            from,
            to,
        });

        const totalLinkContactsCount = parseInt(linkContactsList.headers[TotalEventHeaders.TotalCount], 10) || 0;

        const urlQueryParams: UrlSearchParams = {
            page: activePage,
            perPage,
            ...(from ? { from: dayjs(from).format(SHORT_DATE_FORMAT) } : {}),
            ...(to ? { to: dayjs(to).format(SHORT_DATE_FORMAT) } : {}),
        };

        yield put(Push({ search: stringify(urlQueryParams as any) }));

        const linkContactsPagination = {
            activePage,
            perPage: limit,
            from,
            to,
        };

        yield put(LoadLinkContactsSuccess(linkContactsList?.data, totalLinkContactsCount, linkContactsPagination));
    } catch (err) {
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${err.response.data.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(LoadLinkContactsError());
    }
}
function* watchLinkContactsList(): any {
    yield takeLatest('@@reports/LINK_CONTACTS_START', fetchLinkContactsList);
}

function* fetchLinkContactsAllList(action: LinkContactsAllStartAction): any {
    try {
        const { payload } = action;
        const { from, to } = payload;

        const linkContactsAllList = yield call(getLinkContacts, {
            channelName: payload.channelName,
            link: payload.link,
            from,
            to,
        });

        yield put(LoadLinkContactsAllSuccess(linkContactsAllList?.data));
    } catch (err) {
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${err.response.data.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(LoadLinkContactsAllError());
    }
}
function* watchLinkContactsAllList(): any {
    yield takeLatest('@@reports/LINK_CONTACTS_ALL_START', fetchLinkContactsAllList);
}

function* exportLinkContact(action: ExportLinkContactsAction): any {
    const { payload } = action;
    let checkExportStatus = true;

    try {
        const resp = yield call(exportLinkContacts, payload);

        while (checkExportStatus) {
            yield delay(5000);
            const statusResp = yield call(checkTaskStatus, resp.data.PublicExportID);
            const status: ExportStatus = statusResp.data;

            if (status === ExportStatus.Ready) {
                checkExportStatus = false;
            }

            if (status === ExportStatus.Error || status === ExportStatus.Expired) {
                throw new Error(statusResp);
            }
        }

        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('contactsexported'),
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(Push(ExportsPaths.Exports));
    } catch (e) {
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}
function* watchExportLinkContacts(): any {
    yield takeLatest('@@reports/EXPORT_LINK_CONTACTS', exportLinkContact);
}

function* resend(action: ResendStartAction): any {
    const { payload } = action;

    try {
        yield call(resendByMessageid, { ...payload.params });
        yield put(
            ShowToast({
                severity: 'success',
                message: i18n.t('youremailsubmittedsuccessfully'),
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(ResendSuccess());
    } catch (e) {
        yield put(ResendError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchResend(): any {
    yield takeLatest('@@reports/RESEND_START', resend);
}

function* loadLocationStatsByName(action: GetReportsLocationStatsByNameStartAction): any {
    const { payload } = action;

    try {
        const resp = yield call(getReportsLocationStatsByName, payload);
        const totalCount = Number(resp.headers[TotalEventHeaders.TotalCount]) || 0;
        const response = { data: resp.data, count: totalCount };

        const { isDisabledAddingPaginationParams } = action.payload;

        if (!isDisabledAddingPaginationParams) {
            const { offset, limit } = payload;
            const activePage = (offset + limit) / limit;

            const urlQueryParams: UrlSearchParams = {
                page: activePage,
                perPage: limit,
            };

            if (activePage) {
                yield put(Push({ search: stringify(urlQueryParams) }));
            }
        }

        yield put(GetReportsLocationStatsByNameSuccess(response));
    } catch (e) {
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchLoadLocationStatsByName(): any {
    yield takeLatest('@@reports/GET_REPORTS_LOCATION_STATS_BY_NAME_START', loadLocationStatsByName);
}

const reportSagas = [
    fork(watchLoadEventsLog),
    fork(watchLoadReports),
    fork(watchLoadLinkClicks),
    // fork(watchLoadReportsSummary),
    // fork(watchGetLinkTrackingList),
    fork(watchExportLinks),
    fork(watchLinkContactsList),
    fork(watchExportLinkContacts),
    fork(watchLoadLocationStatsByName),
    // fork(watchGetLinkTrackingAbSplit),
    fork(watchLinkContactsAllList),
    // fork(watchExportEvents),
    fork(watchLoadEventsLogByChannelName),
    fork(watchResend),
];

export default reportSagas;
